import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { Download, Loader2, RefreshCw } from "lucide-react";
import MainLayout from "../layouts/MainLayout";

// Converts lastBackupTime (ISO string) into a readable local date/time.
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Converts minutesOld into "Xh Ym ago" / "Xd Yh ago" instead of a raw number.
const formatAge = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes <= 0) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const totalHours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (totalHours < 24) return `${totalHours}h ${remMinutes}m ago`;
  const days = Math.floor(totalHours / 24);
  return `${days}d ${totalHours % 24}h ago`;
};

const STATUS_BADGE = {
  Ok: "bg-green-100 text-green-700",
  Delayed: "bg-amber-100 text-amber-700",
  Critical: "bg-red-100 text-red-700",
  Failed: "bg-rose-100 text-rose-800",
  "Not Received": "bg-slate-200 text-slate-600",
};

const getBadgeClass = (status) => STATUS_BADGE[status] || "bg-slate-100 text-slate-600";

const BackupData = () => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tracks which single row is currently downloading, so only that row's
  // button shows a spinner instead of disabling/spinning the whole table.
  const [downloadingId, setDownloadingId] = useState(null);

  const getOutletList = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get("/BackupDashboard/GetDashboard");
      setOutlets(response.data || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load backup data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOutletList();
  }, []);

  const handleDownload = async (item) => {
    const { outletId, outletName, downloadUrl } = item;

    try {
      setDownloadingId(outletId);

      // If the dashboard already gave us a direct download URL, use it
      // straight away instead of hitting a separate endpoint.
      if (downloadUrl) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      // Fallback: fetch the file as a blob from the download endpoint.
      const response = await axiosInstance.get(
        `/BackupDownload/Download/${outletId}`,
        { responseType: "blob" }
      );

      const disposition = response.headers["content-disposition"];
      let filename = `${outletName.replace(/\s+/g, "_")}_backup`;
      const match = disposition && disposition.match(/filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i);
      if (match?.[1]) filename = decodeURIComponent(match[1]);

      const blob = new Blob([response.data], {
        type: response.data.type || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert(`Unable to download backup for ${outletName}.`);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <MainLayout>
      <div className="flex flex-col h-full p-3 md:p-6">
        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-slate-200">
          {/* Topbar - min-h on mobile (stacked layout needs more room),
              locked to an exact height only once md:flex-row kicks in */}
          <div className="flex-shrink-0 min-h-[88px] md:h-[76px] flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 px-4 md:px-6 py-3 md:py-0 border-b">
            <div className="leading-tight">
              <h1 className="text-lg md:text-xl font-bold text-slate-800">
                Backup Data
              </h1>
              <p className="text-xs md:text-sm text-slate-500">
                Manage and download outlet backups
              </p>
            </div>

            <button
              onClick={getOutletList}
              disabled={loading}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg transition w-full md:w-auto"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="flex-shrink-0 mx-4 md:mx-6 mt-3 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Mobile-only hint since the table stays wider than a phone
              screen by design (Excel-style dense columns) */}
          <p className="flex-shrink-0 md:hidden px-4 py-1.5 text-[11px] text-slate-400 bg-slate-50 border-b">
            ← Swipe to see more columns
          </p>

          <div className="flex-1 min-h-0 overflow-auto scrollbar-hide">
            <table className="w-full min-w-[820px] border-collapse text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-300">
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet ID
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet Name
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Last Backup Time
                  </th>
                  <th className="px-2 py-1.5 text-right font-semibold border border-slate-200">
                    File Size (MB)
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Age
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Download
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && outlets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500 border border-slate-200">
                      Loading...
                    </td>
                  </tr>
                ) : outlets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500 border border-slate-200">
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  outlets.map((item, idx) => (
                    <tr
                      key={item.outletId}
                      className={`border-b border-slate-200 hover:bg-slate-50 transition ${
                        idx % 2 === 1 ? "bg-slate-50/50" : "bg-white"
                      }`}
                    >
                      <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                        {item.outletId}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                        {item.outletName}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                        {formatDateTime(item.lastBackupTime)}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-right whitespace-nowrap">
                        {item.lastFileSizeMB ? item.lastFileSizeMB.toFixed(2) : "0.00"}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-center whitespace-nowrap">
                        {formatAge(item.minutesOld)}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${getBadgeClass(
                            item.backupStatus
                          )}`}
                        >
                          {item.backupStatus}
                        </span>
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-center">
                        {/* <button
                          onClick={() => handleDownload(item)}
                          disabled={!item.fileExists || downloadingId === item.outletId}
                          title={!item.fileExists ? "No backup file available" : "Download backup"}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-[11px] font-medium transition"
                        >
                          {downloadingId === item.outletId ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                          {downloadingId === item.outletId ? "Downloading" : "Download"}
                        </button> */}

                          <button
                          onClick={() => handleDownload(item)}
                          disabled={downloadingId === item.outletId}
                          title={!item.fileExists ? "No backup file available" : "Download backup"}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-[11px] font-medium transition"
                        >
                          {downloadingId === item.outletId ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                          {downloadingId === item.outletId ? "Downloading" : "Download"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex-shrink-0 px-4 md:px-6 py-2 border-t bg-slate-50 text-xs text-slate-600">
            Total Records : <strong>{outlets.length}</strong>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </MainLayout>
  );
};

export default BackupData;